<script setup lang="ts">
import InputTextGroup from '@/components/forms/groups/InputTextGroup.vue';
import DropdownGroup from '@/components/forms/groups/DropdownGroup.vue';
import FormPhoneInput from '@/components/forms/FormPhoneInput.vue';
import OnboardingInvitedButtons from './OnboardingInvitedButtons.vue';
import Checkbox from '@/components/Checkbox.vue';

import { computed, ref } from 'vue';
import { useStore } from 'vuex';
import { useForm } from 'vee-validate';
import { yup } from '@clearbanc/validation';
import { JobTitlesByCorpCountry } from '@/data/owner';
import {
  CountryCodeEntryWithPhone,
  getAllCallingCodes,
} from '@/utils/phone-numbers';
import { CountryCodes } from '@clearbanc/data-common-types';
import { parsePhoneNumberWithError } from 'libphonenumber-js';
import { JobTitles, UserResponseDto } from '@clearbanc/baus-sdk';
import * as Sentry from '@sentry/vue';

// ------- Props -------
export interface UserData {
  firstName: string;
  lastName: string;
  jobTitle: JobTitles;
  phoneNumber: string;
  agreedToKyc: boolean;
}
interface Props {
  onSubmit: (userData: UserData) => Promise<boolean>;
}
const props = defineProps<Props>();

// ------- Store & State -------
const store = useStore();
const nextButtonRef = ref<InstanceType<typeof OnboardingInvitedButtons> | null>(
  null,
);

// ------- Form Types & Validation -------
interface FormValues {
  firstName: string;
  lastName: string;
  jobTitle?: JobTitles;
  countryCallingCode?: CountryCodeEntryWithPhone;
  phoneNumber: string;
  agreedToKyc: boolean;
}

const validationSchema = yup.object({
  firstName: yup.userName(),
  lastName: yup.userName(),
  jobTitle: yup.string(),
  phoneNumber: yup.phone('countryCallingCode'),
  agreedToKyc: yup
    .boolean()
    .oneOf([true], 'You must agree to the Identity Verification Policy.'),
});

// ------- Computed Properties -------
const user = computed<UserResponseDto>(() => store.getters.user);

const jobTitles = computed(() =>
  JobTitlesByCorpCountry(
    store.getters.business?.corpCountry || CountryCodes.US,
  ),
);

const countryCallingCodes = getAllCallingCodes();

// ------- Methods -------
const getInitialFormValues = (): FormValues => {
  const initialValues: FormValues = {
    firstName: '',
    lastName: '',
    countryCallingCode: countryCallingCodes.find(
      (item) => item.value === CountryCodes.US,
    ),
    phoneNumber: '',
    agreedToKyc: false,
  };

  if (user.value) {
    initialValues.firstName = user.value.firstName || '';
    initialValues.lastName = user.value.lastName || '';

    initialValues.jobTitle = user.value.jobTitle;

    if (user.value.phoneNumber) {
      try {
        const parsedPhoneNumber = parsePhoneNumberWithError(
          user.value.phoneNumber,
          (user.value.address?.country as CountryCodes) ?? CountryCodes.US,
        );
        const callingCode = countryCallingCodes.find(
          (item) => item.value === parsedPhoneNumber?.country,
        );
        if (callingCode && parsedPhoneNumber) {
          initialValues.countryCallingCode = callingCode;
          initialValues.phoneNumber =
            parsedPhoneNumber.nationalNumber.toString();
        }
      } catch (error) {
        Sentry.captureException(error);
      }
    }

    initialValues.agreedToKyc = user.value.agreedToKyc ?? false;
  }
  return initialValues;
};

// ------- Form Setup -------
const { meta, values, validate } = useForm<FormValues>({
  validationSchema,
  initialValues: getInitialFormValues(),
});

const hasErrors = computed(() => !meta.value.valid);

const onSubmit = async () => {
  if (!values.countryCallingCode || !values.jobTitle) {
    return false;
  }

  const phoneNumber = `+${values.countryCallingCode.callingCode}${values.phoneNumber}`;

  return await props.onSubmit({
    firstName: values.firstName,
    lastName: values.lastName,
    jobTitle: values.jobTitle,
    phoneNumber,
    agreedToKyc: values.agreedToKyc,
  });
};
</script>

<template>
  <form class="c-form" @submit.prevent="onSubmit">
    <div class="c-group px-1">
      <!-- Row 1: First Name -->
      <div class="c-form__row">
        <div class="field-wrapper">
          <InputTextGroup name="firstName" label="Legal First Name" />
        </div>
      </div>

      <!-- Row 2: Last Name -->
      <div class="c-form__row">
        <div class="field-wrapper">
          <InputTextGroup name="lastName" label="Legal Last Name" />
        </div>
      </div>

      <!-- Row 3: Job Title -->
      <div class="c-form__row">
        <div class="field-wrapper">
          <DropdownGroup
            name="jobTitle"
            label="Job Title"
            option-label="label"
            option-value="value"
            :options="jobTitles"
            placeholder="Select job title"
          />
        </div>
      </div>

      <!-- Row 4: Phone -->
      <div class="c-form__row">
        <FormPhoneInput
          :dropdown-attrs="{
            name: 'countryCallingCode',
            options: countryCallingCodes,
            optionLabel: 'nameString',
            placeholder: 'Select',
            filter: true,
          }"
          :input-attrs="{
            name: 'phoneNumber',
            label: 'Phone',
          }"
        />
      </div>

      <!-- Row 5: Identity Verification Consent -->
      <div class="c-form__row">
        <Checkbox
          name="agreedToKyc"
          :model-value="values.agreedToKyc"
          @input="
            async (value: boolean) => {
              values.agreedToKyc = value;
              await validate(); // Validate the form after checkbox change
            }
          "
        >
          The information you provide may be shared with third parties to verify
          your identity, comply with law and prevent fraud. You consent to its
          collection, access, use and disclosure per our
          <a
            href="https://clear.co/privacy"
            target="_blank"
            rel="noopener noreferrer"
            >Privacy Policy</a
          >.
        </Checkbox>
      </div>

      <!-- Next Button -->
      <div class="c-form__row mt-8">
        <OnboardingInvitedButtons
          ref="nextButtonRef"
          :on-next="onSubmit"
          :disabled="hasErrors"
        />
      </div>
    </div>
  </form>
</template>

<style scoped>
.c-form__row + .c-form__row {
  margin-top: 1rem;
}

.field-wrapper {
  flex: 1;
}
</style>
